/**
 * EditorContext - manages ProseMirror editor instances
 */
import { uiLogger } from '@annote/core/utils/debug';
import React, { createContext, useContext, ReactNode, useCallback, useEffect, useRef } from 'react';
import { UUID, Note } from '@annote/types';
import { applySuggestion, TextSuggestion } from 'prosemirror-suggestion-mode';
import {
  createEditor,
  EditorView,
  highlightReferencesPlugin,
  updateHighlightDecorations,
} from '@annote/core/components/proseEditor';
import { initialize as initializeProseMirror } from '@annote/core/components/proseEditor/db';
import { useDataContext } from 'src/context/DataContext';
import { getMarkdownFromView } from '@annote/core/components/proseEditor/utils';
import { useBrainContext } from 'src/context/BrainContext';
import { useSettings } from 'src/context/SettingsContext';
import { useHighlightedReferences } from 'src/context/HighlightedReferencesContext';
// Define the structure for a suggestion request
export interface AddSuggestionsOptions {
  noteId: UUID;
  suggestions: TextSuggestion[];
  username?: string;
}

// Define the context type
export interface EditorContextType {
  addSuggestions: (options: AddSuggestionsOptions) => {
    successful: number;
    failed: TextSuggestion[];
  };
  getOrCreateEditor: (element: HTMLElement, note: Note, readOnly: boolean, enableSuggestions: boolean) => EditorView;
  removeEditor: (noteId: UUID) => void;
  saveEditorContent: (noteId: UUID) => Promise<void>;
}

// Create the context
// Export it so other modules can use useContext directly to avoid circular dependencies
export const EditorContext = createContext<EditorContextType | undefined>(undefined);

// Create the provider component
export const EditorProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { annoteDB } = useDataContext();
  const { updateNotes, getCachedNote } = useBrainContext();
  const { getSetting } = useSettings();
  const { highlightedReferences } = useHighlightedReferences();

  // Use ref to hold editor instances so they persist across renders
  const editorsRef = useRef<Map<UUID, EditorView>>(new Map());

  // set annoteDB to my getDB function to work with the proseMirror core lib
  useEffect(() => {
    if (annoteDB) {
      initializeProseMirror(annoteDB);
    }
  }, [annoteDB]);

  const saveEditorContent = useCallback(
    async (noteId: UUID): Promise<void> => {
      const editorViewRef = editorsRef.current.get(noteId);
      if (!editorViewRef) return;

      const newMarkdown = getMarkdownFromView(editorViewRef);

      const noteToUpdate = getCachedNote(noteId);
      if (!noteToUpdate || noteToUpdate.value === newMarkdown) return;

      // Update the note with new content
      await updateNotes([{ ...noteToUpdate, value: newMarkdown }]);
    },
    [updateNotes, getCachedNote],
  );
  // Add the suggestions to the editor displaying them using prosemirror-suggestion-mode
  const addSuggestions = useCallback(
    ({
      noteId,
      suggestions,
      username,
    }: AddSuggestionsOptions): {
      successful: number;
      failed: TextSuggestion[];
    } => {
      const editorViewRef = editorsRef.current.get(noteId);
      if (!editorViewRef) {
        uiLogger.error('No view found for noteId', noteId);
        return { successful: 0, failed: suggestions };
      }

      const results = suggestions.map((suggestion) => ({
        suggestion,
        success: applySuggestion(editorViewRef, suggestion, username || 'Anni'),
      }));

      // saveEditorContent(noteId);
      return {
        successful: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).map((r) => r.suggestion),
      };
    },
    [],
  );

  const getOrCreateEditor = useCallback(
    (element: HTMLElement, note: Note, isReadOnly: boolean, enableSuggestions: boolean): EditorView => {
      // Get spellcheck setting
      const spellCheck = getSetting<boolean>('editor.spellCheck');

      // Create highlight plugin that reads from AIContext
      const highlightPlugin = highlightReferencesPlugin({
        note,
        getHighlightedReferences: () => highlightedReferences,
      });

      // For readonly editors, always create a new instance without tracking it
      if (isReadOnly) {
        // Create with readonly flag and minimal features (no suggestions)
        return createEditor(element, note, { plugins: [highlightPlugin] }, true, false, spellCheck);
      }

      // For editable editors, check if we already have one for this note
      if (editorsRef.current.has(note.id)) {
        const editorViewRef = editorsRef.current.get(note.id);
        // Update the note reference to ensure we're working with latest data
        if (editorViewRef) {
          return editorViewRef;
        }
      }

      // Create a new editable editor if one doesn't exist
      uiLogger.debug('Creating new editable editor for', note.id);
      const view = createEditor(element, note, { plugins: [highlightPlugin] }, false, enableSuggestions, spellCheck);

      // Store the editor and note reference (only for editable editors)
      editorsRef.current.set(note.id, view);
      return view;
    },
    [getSetting, highlightedReferences],
  );

  const removeEditor = useCallback((noteId: UUID): void => {
    // We only track editable editors, so we only need to clean up those
    const editorViewRef = editorsRef.current.get(noteId);
    if (editorViewRef) {
      editorViewRef.destroy();
      editorsRef.current.delete(noteId);
    }
  }, []);

  // Update decorations in all editors when highlighted references change
  useEffect(() => {
    editorsRef.current.forEach((view) => {
      updateHighlightDecorations(view);
    });
  }, [highlightedReferences]);

  return (
    <EditorContext.Provider
      value={{
        addSuggestions,
        getOrCreateEditor,
        removeEditor,
        saveEditorContent,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
};

// Create a hook for easy use
export const useEditorContext = (): EditorContextType => {
  const context = useContext(EditorContext);
  if (context === undefined) {
    throw new Error('useEditorContext must be used within an EditorProvider');
  }
  return context;
};
